import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const Watermark = ({ children, text = 'KRAYNE AGENCY', className = '', onClick = null }) => {
  const [showModal, setShowModal] = useState(false);
  
  // Gérer la touche Échap
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showModal) {
        setShowModal(false);
      }
    };
    
    if (showModal) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [showModal]);
  
  // Pattern avec texte répété et espacé (plus petit, remplit mieux)
  const watermarkPattern = Array.from({ length: 16 })
    .map(() => text)
    .join('  ');

  // Générer une grille dense de filigrames (plus visible, empêche téléchargement)
  const watermarks = Array.from({ length: 35 }).map((_, i) => {
    const row = Math.floor(i / 7);
    const col = i % 7;
    return (
      <div
        key={i}
        className="font-bold text-xs sm:text-sm md:text-lg whitespace-nowrap select-none absolute"
        style={{
          transform: `rotate(-45deg) translate(${col * 100 - 120}px, ${row * 80 - 100}px)`,
          color: 'rgba(255, 255, 255, 0.25)',
          textShadow: '0 0 15px rgba(0, 0, 0, 0.8), 0 0 6px rgba(0, 0, 0, 0.6)',
          letterSpacing: '0.1em',
          WebkitTextStroke: '0.5px rgba(255, 255, 255, 0.15)',
          textTransform: 'uppercase',
          fontWeight: '800',
          pointerEvents: 'none',
          opacity: 1,
        }}
      >
        {watermarkPattern}
      </div>
    );
  });

  const handleImageClick = (e) => {
    // Vérifier si c'est une image
    if (e.target.tagName === 'IMG') {
      setShowModal(true);
    }
  };

  return (
    <>
      <div 
        className={`relative overflow-hidden group cursor-zoom-in ${className}`}
        onContextMenu={(e) => e.preventDefault()}
        onDragStart={(e) => e.preventDefault()}
        onMouseDown={(e) => {
          if (e.button === 2) e.preventDefault(); // Right click
        }}
        onClick={handleImageClick}
        style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
      >
        {/* Contenu enfant avec effet subtle au survol */}
        <div 
          className="relative select-none group-hover:brightness-105 transition-all duration-300"
          style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
        >
          {children}
          {/* Overlay subtle pour plus de contraste */}
          <div className="absolute inset-0 bg-gradient-to-br from-black/4 via-transparent to-black/4 pointer-events-none" />
        </div>
        
        {/* Grille dense de filigrames */}
        <div className="absolute inset-0 pointer-events-none z-20 overflow-hidden">
          {watermarks}
        </div>

        {/* Overlay plus visible au survol */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/12 transition-colors duration-300 z-15 pointer-events-none" />
      </div>

      {/* Modal d'aperçu au clic */}
      {showModal && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={() => setShowModal(false)}
          onContextMenu={(e) => e.preventDefault()}
        >
          <div 
            className="relative max-w-4xl max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Bouton fermeture */}
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 bg-white/20 hover:bg-white/40 text-white p-2 rounded-full transition-colors z-10"
              title="Fermer (Échap)"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Contenu modal - même contenu avec watermarks */}
            <div 
              className="relative w-full h-full max-h-[85vh] overflow-auto"
              onContextMenu={(e) => e.preventDefault()}
              onDragStart={(e) => e.preventDefault()}
              style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
            >
              {children && React.cloneElement(children, {
                style: { ...children.props.style, maxHeight: '85vh', objectFit: 'contain' }
              })}
              
              {/* Filigrames aussi dans le modal (très dense pour empêcher screenshot) */}
              <div className="absolute inset-0 pointer-events-none z-20 overflow-hidden">
                {Array.from({ length: 50 }).map((_, i) => {
                  const row = Math.floor(i / 10);
                  const col = i % 10;
                  return (
                    <div
                      key={`modal-${i}`}
                      className="font-bold text-sm md:text-base lg:text-lg whitespace-nowrap select-none absolute"
                      style={{
                        transform: `rotate(-45deg) translate(${col * 80 - 100}px, ${row * 70 - 100}px)`,
                        color: 'rgba(255, 255, 255, 0.3)',
                        textShadow: '0 0 20px rgba(0, 0, 0, 1), 0 0 8px rgba(0, 0, 0, 0.8)',
                        letterSpacing: '0.12em',
                        WebkitTextStroke: '0.6px rgba(255, 255, 255, 0.2)',
                        textTransform: 'uppercase',
                        fontWeight: '900',
                        pointerEvents: 'none',
                        opacity: 1,
                      }}
                    >
                      {watermarkPattern}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Info au survol */}
            <p className="absolute bottom-4 left-4 text-white/60 text-sm pointer-events-none">
              Cliquez pour fermer • Protégé par filigrane
            </p>
          </div>
        </div>
      )}

      {/* Style pour désactiver le drag et drop et le menu contextuel */}
      <style>{`
        .group img {
          pointer-events: none;
          -webkit-user-drag: none;
          user-select: none;
          -webkit-user-select: none;
        }
        
        [oncontextmenu] {
          -webkit-touch-callout: none;
        }
      `}</style>
    </>
  );
};

export default Watermark;